/* global modForm Modalytics */
/**
 * Fetches Trestle Phone Intel `activity_score` for a US phone number and stores it on `modForm`.
 *
 * **Caching**: Scores are keyed by normalized digits in `modForm.phoneActivityScoreByPhoneNumber`.
 * Cache hits set `modForm.phoneActivityScore` and skip the network call.
 *
 * **Side effects**:
 * - Sets `modForm.phoneActivityScore` (current score or null while fetching / on failure)
 * - Sets `modForm.checkingActivityScoreForPhoneNumber` to the raw `phone` arg during fetch; cleared on success
 * - Heap: `Trestle API` events (success, http error, no score, network error)
 *
 * @param {string} phone - Phone value (formatted or digits); non-digits are stripped
 * @param {boolean} [sendActivityContextToHeap=false] - When true and min score opt is set, adds Heap `context` of `low activity score` or `high activity score`
 * @returns {Promise<void>}
 */

function trackTrestleSuccess(score, context) {
	const props = { status: 'success', score };
	if (context) {
		props.context = context;
	}
	Modalytics.heap('track', ['Trestle API', props]);
}

export async function getTrestlePhoneValidityScore(phone, sendActivityContextToHeap = true) {
	const phoneNumber = phone.replace(/\D/g, '');
	const trestleApiUrl = `https://api.trestleiq.com/3.0/phone_intel?phone=${phoneNumber}`;

	if (!modForm.phoneActivityScoreByPhoneNumber) {
		modForm.phoneActivityScoreByPhoneNumber = {};
	}
	if (modForm.phoneActivityScoreByPhoneNumber[phoneNumber] !== undefined) {
		modForm.phoneActivityScore = modForm.phoneActivityScoreByPhoneNumber[phoneNumber];
		trackTrestleSuccess(modForm.phoneActivityScore, 'used stored response');
		return;
	}

	try {
		modForm.phoneActivityScore = null;
		modForm.checkingActivityScoreForPhoneNumber = phone;

		const response = await fetch(trestleApiUrl, {
			method: 'GET',
			headers: {
				'x-api-key': 'ilMM8fMQrE1jF7fwbzVdX9vNcQA5BsRO5GyLQ8pk'
			}
		});

		if (!response.ok) {
			Modalytics.heap('track', ['Trestle API', { status: `http error: ${response.status}` }]);
		}

		const data = await response.json();
		const score = data.activity_score;
		if (score !== undefined) {
			modForm.phoneActivityScore = score;
			modForm.phoneActivityScoreByPhoneNumber[phoneNumber] = score;
			if (modForm.opts.trestlePhoneValidityMinActivityScore && sendActivityContextToHeap) {
				if (score <= modForm.opts.trestlePhoneValidityMinActivityScore) {
					trackTrestleSuccess(score, 'low activity score');
				} else {
					trackTrestleSuccess(score, 'high activity score');
				}
			} else {
				trackTrestleSuccess(score);
			}
		} else {
			Modalytics.heap('track', ['Trestle API', { status: 'no activity score found' }]);
		}
	} catch (err) {
		console.error('Fetch error:', err);
		Modalytics.heap('track', ['Trestle API', { status: 'network error'}]);
	} finally {
		modForm.checkingActivityScoreForPhoneNumber = null;
	}
}